import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '@/redux/store'
import {
  setLoading,
  setError,
  setConfig,
  setSession,
  setMyProfile,
  updateMyProfileField,
  setTeamProfiles,
  setTeamStatuses,
  setUnassignedMembers,
  setRenegotiationRequest,
  setCharterVersions,
  setGateStatus,
  setRoundStatus,
  setRoundControlLoading,
  updateSessionStatus,
} from '../slices/charterSlice'
import {
  setPendingRenegotiationRequest,
  setRenegotiationMode,
  setGroupCharterStatus,
} from '../slices/charterGroupWsSlice'
import { setMessages as setPrepMessages } from '../slices/charterPrepWsSlice'
import type { PrepChatMessage } from '../types/charterWsTypes'
import * as charterApi from '../../api/charter'
import type { CharterRoundAction } from '../../api/charter'

// ──────────────────────────────────────────────────────────
// Config
// ──────────────────────────────────────────────────────────

export const fetchCharterConfig = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchConfig', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true))
    const res = await charterApi.getCharterConfig(roomId)
    dispatch(setConfig(res.data))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load charter config'
    dispatch(setError(msg))
    return rejectWithValue(msg)
  } finally {
    dispatch(setLoading(false))
  }
})

export const saveCharterConfig = createAsyncThunk<
  void,
  { roomId: string; data: Record<string, unknown> },
  { dispatch: AppDispatch; state: RootState }
>('charter/saveConfig', async ({ roomId, data }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true))
    const res = await charterApi.updateCharterConfig(roomId, data)
    dispatch(setConfig(res.data))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to save charter config'
    dispatch(setError(msg))
    return rejectWithValue(msg)
  } finally {
    dispatch(setLoading(false))
  }
})

// ──────────────────────────────────────────────────────────
// Charter Session
// ──────────────────────────────────────────────────────────

export const fetchCharterSession = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchSession', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getCharterSession(roomId)
    dispatch(setSession(res.data ?? null))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load charter session'
    return rejectWithValue(msg)
  }
})

export const fetchCharterStatus = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchStatus', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getCharterStatus(roomId)
    dispatch(setTeamStatuses(res.data ?? []))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load charter status'
    return rejectWithValue(msg)
  }
})

/**
 * The whole roster in one call: teams and the students not yet placed.
 *
 * Supersedes fetchCharterStatus for the monitoring panel, which needs both.
 * fetchCharterStatus stays for callers that only care about teams.
 */
export const fetchCharterRoster = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchRoster', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getCharterRoster(roomId)
    dispatch(setTeamStatuses(res.data?.teams ?? []))
    dispatch(setUnassignedMembers(res.data?.unassigned ?? []))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load the roster'
    return rejectWithValue(msg)
  }
})

export const fetchCharterGateStatus = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchGateStatus', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getCharterGateStatus(roomId)
    dispatch(setGateStatus(res.data ?? null))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load gate status'
    return rejectWithValue(msg)
  }
})

// ──────────────────────────────────────────────────────────
// Round 0 controls (professor, room-wide)
// ──────────────────────────────────────────────────────────

export const charterRoundControl = createAsyncThunk<
  void,
  { roomId: string; action: CharterRoundAction },
  { dispatch: AppDispatch; state: RootState }
>(
  'charter/roundControl',
  async ({ roomId, action }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setRoundControlLoading(action))
      const res = await charterApi.charterRoundControl(roomId, action)
      if (res.data?.round_status) {
        dispatch(setRoundStatus(res.data.round_status))
      }
      if (res.data?.teams) {
        dispatch(setTeamStatuses(res.data.teams))
      }
      // Refresh the gate readout (ratified counts may have changed).
      dispatch(fetchCharterGateStatus({ roomId }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to run charter control'
      dispatch(setError(msg))
      return rejectWithValue(msg)
    } finally {
      dispatch(setRoundControlLoading(null))
    }
  }
)

// ──────────────────────────────────────────────────────────
// Profile
// ──────────────────────────────────────────────────────────

export const fetchMyProfile = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchMyProfile', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getMyProfile(roomId)
    dispatch(setMyProfile(res.data ?? null))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load profile'
    return rejectWithValue(msg)
  }
})

export const approveMyProfile = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/approveMyProfile', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    await charterApi.approveMyProfile(roomId)
    dispatch(updateMyProfileField({ profile_approved: true }))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to approve profile'
    dispatch(setError(msg))
    return rejectWithValue(msg)
  }
})

/** Student-accessible thunk — fetches public teammate summaries for Phase 2 sidebar. */
export const fetchMyTeamSummaries = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchMyTeamSummaries', async ({ roomId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getMyTeamSummaries(roomId)
    dispatch(setTeamProfiles(res.data ?? []))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load team summaries'
    return rejectWithValue(msg)
  }
})

// ──────────────────────────────────────────────────────────
// Export
// ──────────────────────────────────────────────────────────

export const exportCharter = createAsyncThunk<
  void,
  { roomId: string; teamId: number; version?: number; isDraft?: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  'charter/export',
  async ({ roomId, teamId, version, isDraft }, { rejectWithValue }) => {
    try {
      const res = await charterApi.exportCharter(roomId, teamId, version, isDraft)
      // Trigger browser download
      const blob = new Blob([res.data as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = isDraft ? 'team-charter-draft.docx' : `team-charter-v${version ?? 1}.docx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to export charter'
      return rejectWithValue(msg)
    }
  }
)

// ──────────────────────────────────────────────────────────
// Renegotiation
// ──────────────────────────────────────────────────────────

export const fetchCharterVersions = createAsyncThunk<
  void,
  { roomId: string; teamId: number },
  { dispatch: AppDispatch; state: RootState }
>('charter/fetchVersions', async ({ roomId, teamId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await charterApi.getCharterVersions(roomId, teamId)
    dispatch(setCharterVersions(res.data ?? []))
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to load charter versions'
    return rejectWithValue(msg)
  }
})

export const createRenegotiationRequest = createAsyncThunk<
  void,
  { roomId: string; teamId: number; sections_to_amend: string[]; reason: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'charter/createRenegotiationRequest',
  async ({ roomId, teamId, sections_to_amend, reason }, { dispatch, rejectWithValue }) => {
    try {
      const res = await charterApi.createRenegotiationRequest(roomId, teamId, {
        sections_to_amend,
        reason,
      })
      dispatch(setRenegotiationRequest(res.data))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to create amendment request'
      dispatch(setError(msg))
      return rejectWithValue(msg)
    }
  }
)

export const fetchPrepHistory = createAsyncThunk<
  void,
  { roomId: string },
  { dispatch: AppDispatch }
>('charter/fetchPrepHistory', async ({ roomId }, { dispatch }) => {
  const res = await charterApi.getPrepHistory(roomId)
  const messages = res.data.messages as PrepChatMessage[]
  dispatch(setPrepMessages(messages))
})

export const voteRenegotiationThunk = createAsyncThunk<
  void,
  { roomId: string; teamId: number; vote: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  'charter/voteRenegotiation',
  async ({ roomId, teamId, vote }, { dispatch, rejectWithValue }) => {
    try {
      const res = await charterApi.voteRenegotiation(roomId, teamId, vote)
      dispatch(setRenegotiationRequest(res.data))
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      // 404 = the request was already decided (a duplicate/late vote). Treat it
      // as a no-op instead of surfacing a spurious error to the voter.
      if (status === 404) {
        return
      }
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || 'Failed to record vote'
      dispatch(setError(msg))
      return rejectWithValue(msg)
    }
  }
)

// HTTP (not WebSocket) so cancel is handled off the group consumer's dispatch
// loop — instant even while the moderator is streaming. Idempotent server-side.
export const cancelRenegotiation = createAsyncThunk<
  void,
  { roomId: string; teamId: number },
  { dispatch: AppDispatch; state: RootState }
>(
  'charter/cancelRenegotiation',
  async ({ roomId, teamId }, { dispatch, rejectWithValue }) => {
    try {
      await charterApi.cancelRenegotiation(roomId, teamId)
      // Optimistically revert THIS client immediately; the server broadcast
      // (renegotiation_cancelled) confirms it for the other member.
      dispatch(setPendingRenegotiationRequest(null))
      dispatch(setRenegotiationMode(false))
      dispatch(setGroupCharterStatus({ status: 'locked' }))
      dispatch(updateSessionStatus({ status: 'locked' }))
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail || 'Failed to cancel amendment'
      dispatch(setError(msg))
      return rejectWithValue(msg)
    }
  }
)
